import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WiziQuizInstructionsModal from '../components/wizi-quiz/WiziQuizInstructionsModal';
import {
  getActiveWiziQuizzes,
  startWiziQuizAttempt,
  getMyWiziQuizAttempts,
  isWiziQuizSuccess,
  getWiziQuizErrorMessage,
} from '../utils/wiziQuizApiClient';

interface WiziQuiz {
  id: number;
  name: string;
  description: string;
  time_limit: number;
  total_marks: number;
  level: string;
  is_published: boolean;
  valid_from: string | null;
  valid_until: string | null;
  cover_image?: string | null;
  is_locked?: boolean;
  has_attempted?: boolean;
  lock_reason?: 'subscription' | 'sequential' | null;
  user_plan?: string;
}

interface QuizAttempt {
  id: number;
  attempt_number: number;
  attempt_status: string;
  total_score: number;
  accuracy_percentage: number;
  completed_at: string | null;
}

const WiziQuizList: React.FC = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<WiziQuiz[]>([]);
  const [attempts, setAttempts] = useState<{ [quizId: number]: QuizAttempt[] }>({});
  const [loading, setLoading] = useState(true);
  const [startingQuiz, setStartingQuiz] = useState<number | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState<WiziQuiz | null>(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [motivationalQuote, setMotivationalQuote] = useState('');

  const motivationalQuotes = [
    "Don't just prepare for JEE 2026 — prove your readiness. Complete all 10 mock tests sincerely, and success will have no option but to follow you.",
    "These 10 mock tests are not practice — they are your rehearsal for victory. Attempt them honestly, and JEE Main 2026 will reward you.",
    "If you can conquer all 10 mocks with discipline, you've already crossed halfway to cracking JEE Main 2026. Results will follow commitment.",
    "One decision. Ten mock tests. A lifetime-changing result. Give your best to all 10, and let JEE Main 2026 reflect your true potential.",
    "Students who complete all 10 mocks sincerely don't fear the exam — they walk into JEE Main 2026 prepared to succeed.",
    "Attempt all 10 mocks with honesty and discipline — this single decision can turn your preparation into performance and your JEE Main 2026 dream into a guaranteed reality.",
    "Every mock test is a stepping stone to your JEE Main 2026 success. Complete all 10 with dedication, and watch confidence become your strongest weapon.",
    "Champions are made in practice, not on exam day. Master all 10 mocks, and JEE Main 2026 will just be a formality.",
    "Your JEE Main 2026 rank is being decided right now, in these mock tests. Give each one your absolute best — your future self will thank you.",
    "The difference between dreaming and achieving JEE success? Completing all 10 mock tests with the same intensity as the real exam.",
    "Don't wait for motivation — create discipline. Finish all 10 mock tests sincerely, and JEE Main 2026 will be your moment of glory.",
    "Success in JEE Main 2026 is not luck — it's the result of honest preparation. Complete every mock test like it's the final exam.",
    "Your competition is attempting these mocks casually. You? Complete all 10 with full focus and watch yourself rise above them in JEE Main 2026.",
    "Mock tests are mirrors of your preparation. Face all 10 honestly, improve consistently, and JEE Main 2026 will reflect your hard work.",
    "The path to IIT starts here — with these 10 mock tests. Take them seriously, and JEE Main 2026 will be your launchpad to greatness.",
    "You don't need 100 resources. You need 10 sincere mock test attempts. That's the formula for JEE Main 2026 success.",
    "Every mock test you skip is an opportunity you lose. Complete all 10 diligently, and make JEE Main 2026 count.",
    "Winners don't make excuses — they make progress. Attempt all 10 mock tests honestly and become unstoppable by JEE Main 2026.",
    "Your JEE Main 2026 scorecard is being written now, one mock test at a time. Make each attempt count.",
    "The real battle is not on exam day — it's in completing these 10 mock tests with unwavering dedication. Win here, win everywhere.",
    "Excellence is not an accident. It's the result of sincere practice. Complete all 10 mock tests, and excellence will define your JEE Main 2026 result.",
    "Trust the process. Ten honest mock tests today equal one confident performance in JEE Main 2026 tomorrow."
  ];

  useEffect(() => {
    loadQuizzes();
    // Select a random motivational quote
    const randomIndex = Math.floor(Math.random() * motivationalQuotes.length);
    setMotivationalQuote(motivationalQuotes[randomIndex]);
  }, []);

  const loadQuizzes = async () => {
    try {
      const response = await getActiveWiziQuizzes();
      
      if (isWiziQuizSuccess(response)) {
        const fetchedQuizzes = response.result;
        setQuizzes(fetchedQuizzes);

        // Load attempts for each quiz
        for (const quiz of fetchedQuizzes) {
          loadQuizAttempts(quiz.id);
        }
      }
    } catch (error) {
      console.error('Error loading quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuizAttempts = async (quizId: number) => {
    try {
      const response = await getMyWiziQuizAttempts(quizId);
      
      if (isWiziQuizSuccess(response)) {
        setAttempts((prev) => ({
          ...prev,
          [quizId]: response.result,
        }));
      }
    } catch (error) {
      console.error(`Error loading attempts for quiz ${quizId}:`, error);
    }
  };

  const handleStartQuiz = async (quiz: WiziQuiz) => {
    setSelectedQuiz(quiz);
    // You can fetch total questions from API if needed, for now using a default
    setTotalQuestions(quiz.total_marks || 100); // Estimate based on marks or fetch from API
    setShowInstructions(true);
  };

  const handleProceedWithQuiz = async () => {
    if (!selectedQuiz) return;
    
    setShowInstructions(false);
    setStartingQuiz(selectedQuiz.id);
    
    try {
      const response = await startWiziQuizAttempt(selectedQuiz.id);
      
      if (isWiziQuizSuccess(response)) {
        const attempt = response.result;
        navigate(`/mock-test/attempt/${attempt.id}`);
      } else {
        alert(getWiziQuizErrorMessage(response));
      }
    } catch (error) {
      console.error('Error starting quiz:', error);
      alert('Failed to start quiz. Please try again.');
    } finally {
      setStartingQuiz(null);
      setSelectedQuiz(null);
    }
  };

  const handleCancelInstructions = () => {
    setShowInstructions(false);
    setSelectedQuiz(null);
  };

  const handleResumeQuiz = (attemptId: number) => {
    navigate(`/mock-test/attempt/${attemptId}`);
  };

  const handleViewResult = (attemptId: number) => {
    navigate(`/mock-test/result/${attemptId}`);
  };

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const getDifficultyBadge = (level: string) => {
    const badges: { [key: string]: string } = {
      Elementary: 'bg-success',
      Moderate: 'bg-warning',
      Advance: 'bg-danger',
    };
    return badges[level] || 'bg-secondary';
  };

  const getLockMessage = (quiz: WiziQuiz) => {
    if (!quiz.is_locked) return null;
    
    if (quiz.lock_reason === 'subscription') {
      return {
        icon: 'fa-crown',
        text: 'Upgrade to Basic/Premium/Pro to unlock',
        variant: 'info'
      };
    } else if (quiz.lock_reason === 'sequential') {
      return {
        icon: 'fa-lock',
        text: 'Complete previous quizzes to unlock',
        variant: 'warning'
      };
    }
    
    return {
      icon: 'fa-lock',
      text: 'Complete previous quizzes to unlock',
      variant: 'warning'
    };
  };

  const getAttemptStatusBadge = (status: string) => {
    const badges: { [key: string]: string } = {
      in_progress: 'bg-primary',
      completed: 'bg-success',
      abandoned: 'bg-secondary',
      timeout: 'bg-danger',
    };
    return badges[status] || 'bg-secondary';
  };

  const getInProgressAttempt = (quizId: number): QuizAttempt | null => {
    const quizAttempts = attempts[quizId] || [];
    return quizAttempts.find((a) => a.attempt_status === 'in_progress') || null;
  };

  const getBestAttempt = (quizId: number): QuizAttempt | null => {
    const quizAttempts = attempts[quizId] || [];
    const completedAttempts = quizAttempts.filter((a) => a.attempt_status === 'completed');
    
    if (completedAttempts.length === 0) return null;
    
    return completedAttempts.reduce((best, current) => {
      return current.total_score > best.total_score ? current : best;
    });
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading quizzes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
        {motivationalQuote && (
        <div className="position-relative mb-4 overflow-hidden" 
             style={{ 
               background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 50%, #4facfe 100%)',
               borderRadius: '20px',
               boxShadow: '0 20px 60px rgba(245, 87, 108, 0.4)',
               padding: '0',
             }}>
          {/* Animated Background Elements */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'float 8s ease-in-out infinite reverse'
          }}></div>
          
          {/* Content */}
          <div className="position-relative p-2 p-md-2">
            <div className="text-center mb-1">
              
            </div>
            
            <div className="position-relative">
              
              
              <p className="text-center text-white mb-0 px-3 px-md-5" 
                 style={{ 
                   fontSize: '2.0rem',
                   fontWeight: '600',
                   lineHeight: '1.8',
                   textShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                   padding: '0 20px'
                 }}>
                {motivationalQuote}
              </p>
            </div>
          </div>
          
          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              50% { transform: translateY(-20px) translateX(10px); }
            }
          `}</style>
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-clipboard-list me-2 text-primary"></i>
          WiZi Mock Tests
        </h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Dashboard
        </button>
      </div>

      {quizzes.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          No quizzes available at the moment. Please check back later.
        </div>
      ) : (
        <div className="row">
          {quizzes.map((quiz) => {
            const inProgressAttempt = getInProgressAttempt(quiz.id);
            const bestAttempt = getBestAttempt(quiz.id);
            const allAttempts = attempts[quiz.id] || [];
            const completedAttempts = allAttempts.filter((a) => a.attempt_status === 'completed');

            return (
              <div key={quiz.id} className="col-md-6 col-lg-3 mb-4">
                <div className={`card h-100 shadow-sm ${quiz.is_locked ? 'border-secondary' : ''}`}>
                  {quiz.cover_image && (
                    <img
                      src={quiz.cover_image}
                      className="card-img-top"
                      alt={quiz.name}
                      style={{ height: '180px', objectFit: 'cover', padding:'0px', borderRadius:'10px', borderBottomLeftRadius:'0px', borderBottomRightRadius:'0px' }}
                    />
                  )}
                  <div className="card-body">
                    {quiz.is_locked && (() => {
                      const lockMsg = getLockMessage(quiz);
                      return (
                        <div className={`alert alert-${lockMsg?.variant} py-2 mb-3`}>
                          <i className={`fas ${lockMsg?.icon} me-2`}></i>
                          <small>{lockMsg?.text}</small>
                        </div>
                      );
                    })()}
                    <h5 className="card-title text-primary">{quiz.name}</h5>
                    <p className="card-text text-muted small">{quiz.description}</p>

                    <div className="mb-3">
                      <span className={`badge ${getDifficultyBadge(quiz.level)} me-2`}>
                        {quiz.level}
                      </span>
                      <span className="badge bg-info me-2">
                        <i className="fas fa-clock me-1"></i>
                        {quiz.time_limit} mins
                      </span>
                      <span className="badge bg-secondary">
                        <i className="fas fa-star me-1"></i>
                        {quiz.total_marks} marks
                      </span>
                    </div>

                    {/* Attempt Statistics */}
                    {completedAttempts.length > 0 && (
                      <div className="mb-3 p-2 bg-light rounded">
                        <div className="small">
                          <div className="d-flex justify-content-between">
                            <span>Attempts:</span>
                            <strong>{completedAttempts.length}</strong>
                          </div>
                          {bestAttempt && (
                            <>
                              <div className="d-flex justify-content-between">
                                <span>Best Score:</span>
                                <strong className="text-success">{bestAttempt.total_score}</strong>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span>Best Accuracy:</span>
                                <strong className="text-success">
                                  {(typeof bestAttempt.accuracy_percentage === 'number' 
                                    ? bestAttempt.accuracy_percentage 
                                    : parseFloat(bestAttempt.accuracy_percentage)).toFixed(1)}%
                                </strong>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* In Progress Attempt */}
                    {inProgressAttempt && (
                      <div className="alert alert-warning py-2 mb-2">
                        <small>
                          <i className="fas fa-play-circle me-2"></i>
                          Attempt #{inProgressAttempt.attempt_number} in progress
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="card-footer bg-transparent">
                    {inProgressAttempt ? (
                      <button
                        className="btn btn-warning w-100"
                        onClick={() => handleResumeQuiz(inProgressAttempt.id)}
                      >
                        <i className="fas fa-play me-2"></i>
                        Resume Quiz
                      </button>
                    ) : (
                      <button
                        className={`btn w-100 ${
                          quiz.is_locked && quiz.lock_reason === 'subscription'
                            ? 'btn-info'
                            : 'btn-primary'
                        }`}
                        onClick={() => {
                          if (quiz.is_locked && quiz.lock_reason === 'subscription') {
                            handleUpgradeClick();
                          } else {
                            handleStartQuiz(quiz);
                          }
                        }}
                        disabled={
                          startingQuiz === quiz.id ||
                          (quiz.is_locked && quiz.lock_reason !== 'subscription')
                        }
                        title={quiz.is_locked && quiz.lock_reason === 'sequential' ? (getLockMessage(quiz)?.text || 'Locked') : ''}
                      >
                        {quiz.is_locked && quiz.lock_reason === 'subscription' ? (
                          <>
                            <i className="fas fa-crown me-2"></i>
                            Upgrade to Unlock
                          </>
                        ) : quiz.is_locked ? (
                          <>
                            <i className="fas fa-lock me-2"></i>
                            Locked
                          </>
                        ) : startingQuiz === quiz.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Starting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-play-circle me-2"></i>
                            Start New Attempt
                          </>
                        )}
                      </button>
                    )}

                    {/* View Past Results */}
                    {completedAttempts.length > 0 && (
                      <button
                        className="btn btn-outline-secondary w-100 mt-2"
                        onClick={() => {
                          const latestCompleted = completedAttempts[0];
                          handleViewResult(latestCompleted.id);
                        }}
                      >
                        <i className="fas fa-chart-line me-2"></i>
                        View Results
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Instructions Modal */}
      <WiziQuizInstructionsModal
        show={showInstructions}
        quizName={selectedQuiz?.name || ''}
        timeLimit={selectedQuiz?.time_limit || 0}
        totalQuestions={totalQuestions}
        onProceed={handleProceedWithQuiz}
        onCancel={handleCancelInstructions}
      />
    </div>
  );
};

export default WiziQuizList;
